import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import Button from '@/components/Button';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { VideoFactoryCategory, VideoFactorySettings } from '@/types/resources';
import { api } from '@/utils/api';
import { VideoFactorySettingsType } from '@/utils/enums';
import { scrollBarStyles } from '@/utils/theme';

import EditForm from './EditForm';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 2px 0px #00000014;
  border-radius: 4px;
  padding: 32px;
  display: flex;
  align-items: flex-start;
`;

const Content = styled.div`
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: 120px;
`;

const Heading = styled(Text)`
  margin: 0 0 12px;
  > span {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const VersionsButtonWrap = styled.div`
  position: relative;
`;

const VersionsDropdown = styled.div`
  padding: 4px 0;
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 200px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 4px;
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  max-height: 300px;
  overflow-y: auto;
  ${scrollBarStyles};
  display: flex;
  flex-direction: column;
`;

const VersionButton = styled.button.attrs({ type: 'button' })`
  padding: 12px 16px;
  &:hover {
    background: ${({ theme }) => theme.colors.grayscaleLight};
  }

  &:first-child {
    border-top-right-radius: 4px;
    border-top-left-radius: 4px;
  }
  &:last-child {
    border-bottom-right-radius: 4px;
    border-bottom-left-radius: 4px;
  }
`;

interface Props {
  settings: VideoFactorySettings[];
  type: VideoFactorySettingsType;
  activeCategory: VideoFactoryCategory;
}

const renderTitle = (type: VideoFactorySettingsType) => {
  switch (type) {
    case VideoFactorySettingsType.STORY_PROMPT:
      return 'Story Prompt';
    case VideoFactorySettingsType.SCRIPT_PROMPT:
      return 'Script Prompt';
    default:
      return '';
  }
};

const addLineBreak = (str: string) =>
  str.split('\n').map((subStr, i) => {
    return (
      <Fragment key={i}>
        {subStr}
        <br />
      </Fragment>
    );
  });

const SettingsCard: FC<Props> = ({ settings, type, activeCategory }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const [showEditForm, setShowEditForm] = useState(false);

  const [showVersionsDropdown, setShowVersionsDropdown] = useState(false);

  const filtered = useMemo(() => {
    return settings.filter((setting) => setting.type === type);
  }, [settings, type]);

  const [activeSetting, setActiveSetting] = useState(
    filtered.find((setting) => setting.is_active),
  );

  const onActivate = async () => {
    try {
      const confirmed = await alerts.confirm('Are you sure?');

      if (!confirmed) {
        return;
      }

      const setting = await api.post<never, VideoFactorySettings>(
        `/v1/video-factory/settings/${activeSetting?.id}/activate`,
      );

      await queryClient.invalidateQueries(['video_factory_settings']);

      setActiveSetting(setting);

      alerts.close();
    } catch {
      alerts.error();
    }
  };

  useEffect(() => {
    setActiveSetting(filtered.find((setting) => setting.is_active));
  }, [filtered, type]);

  return (
    <Card>
      {activeSetting ? (
        <>
          <Content>
            <Heading $type="heading2">
              <Text $type="caption" $color="gray">
                {DateTime.fromISO(activeSetting!.created_at).toFormat(
                  'yyyy-LL-dd HH:mm',
                )}
              </Text>
              {renderTitle(type)} • V{activeSetting?.version}
            </Heading>
            <Text $type="body" $color="gray">
              {addLineBreak(activeSetting?.data?.prompt)}
            </Text>
          </Content>
          <Actions>
            <Button
              small
              disabled={activeSetting?.is_active}
              onClick={onActivate}
            >
              {activeSetting?.is_active ? 'Activated' : 'Activate'}
            </Button>
            <Button small onClick={() => setShowEditForm(true)}>
              Edit
            </Button>
            <VersionsButtonWrap>
              <Button
                small
                onClick={() => setShowVersionsDropdown((prev) => !prev)}
              >
                Versions
              </Button>
              {showVersionsDropdown && (
                <VersionsDropdown>
                  {filtered.map((setting) => (
                    <VersionButton
                      key={setting.id}
                      onClick={() => {
                        setActiveSetting(setting);
                        setShowVersionsDropdown(false);
                      }}
                    >
                      <Text $type="smallBold" as="span">
                        V{setting.version} •{' '}
                        <Text $type="smallBold" as="span" $color="gray">
                          {DateTime.fromISO(setting.created_at).toFormat(
                            'yyyy-LL-dd HH:mm',
                          )}
                        </Text>
                      </Text>
                    </VersionButton>
                  ))}
                </VersionsDropdown>
              )}
            </VersionsButtonWrap>
          </Actions>
        </>
      ) : (
        <Button small onClick={() => setShowEditForm(true)}>
          Create {renderTitle(type)}
        </Button>
      )}

      <AnimatePresence>
        {showEditForm && (
          <EditForm
            onClose={() => setShowEditForm(false)}
            defaultType={type}
            defaultCategoryId={activeCategory.id}
            setting={activeSetting}
          />
        )}
      </AnimatePresence>
    </Card>
  );
};

export default SettingsCard;
